import React, { useState, useCallback, useRef } from 'react';

/**
 * Resizable sidebar width using pointer drag (mouse + touch).
 * Uses delta from drag start so the handle works when the sidebar does not sit at viewport x=0
 * (e.g. Chrome extension side panel with padding or nested layout).
 */
export const useSidebarResize = (options?: {
  initialWidth?: number;
  minWidth?: number;
  maxWidth?: number;
}) => {
  const initialWidth = options?.initialWidth ?? 200;
  const minWidth = options?.minWidth ?? 120;
  const maxWidth = options?.maxWidth ?? 400;

  const [sidebarWidth, setSidebarWidth] = useState(initialWidth);
  const [isResizing, setIsResizing] = useState(false);

  const boundsRef = useRef({ minWidth, maxWidth });
  boundsRef.current = { minWidth, maxWidth };

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      e.preventDefault();

      const captureTarget = e.currentTarget;
      if (!(captureTarget instanceof HTMLElement)) return;
      if (typeof e.pointerId === 'number') {
        try {
          captureTarget.setPointerCapture(e.pointerId);
        } catch {
          /* already captured or unsupported */
        }
      }

      const startX = e.clientX;
      const startW = sidebarWidth;
      const { minWidth: minW, maxWidth: maxW } = boundsRef.current;

      const onMove = (ev: PointerEvent) => {
        const delta = ev.clientX - startX;
        setSidebarWidth(Math.max(minW, Math.min(maxW, startW + delta)));
      };

      const onUp = () => {
        if (typeof e.pointerId === 'number') {
          try {
            captureTarget.releasePointerCapture(e.pointerId);
          } catch {
            /* noop */
          }
        }
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
        setIsResizing(false);
      };

      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
      setIsResizing(true);

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [sidebarWidth]
  );

  return {
    sidebarWidth,
    isResizing,
    handlePointerDown,
  };
};
